import { AxiosError, AxiosResponse } from 'axios'
import useAxios from '../useAxios'
import { useQueue } from '../useQueue'
import useQueueCancel from './useQueueCancel'
import { normalizeAxiosError } from '../../reducers/errorReducer'
import { StatefulMessage } from '../azurewebsocket/useProcessMessage'
import { AnyQueueData, QueueContextData, QueueJobKind, QueueStatus, QueueStatusData, truncateRequestId } from './queueTypes'

export type QueueDispatch<RequestT, StatusT, ContextT> = (
    requestId: string,
    context?: QueueContextData<RequestT, StatusT, ContextT>,
    status?: Partial<QueueStatusData<StatusT>>,
    restart?: boolean,
) => void

/**
 * Puts a job on the queue and fires the request that kicks it off on the server. From there on the job is driven by
 * status transitions, either from the dispatch response or from websocket notifications, and rendered by the
 * QueueToaster.
 *
 * Note that the status callbacks are stored on the job rather than captured in a closure. That is deliberate: the
 * component that dispatched the work is often unmounted by the time the server reports back, and the notification
 * handler that transitions the job needs to be able to run them anyway.
 */
export default function useQueueDispatch<RequestT, StatusT = object, ContextT = object>(
    kind: QueueJobKind,
): QueueDispatch<RequestT, StatusT, ContextT> {
    const axios = useAxios()
    const queue = useQueue()
    const cancel = useQueueCancel()

    return (requestId, context, status, restart) => {
        const id = truncateRequestId(requestId)

        // The user elected to force a restart, so drop the previous attempt and tell the server to forget about it.
        // This happens before the new job is pushed, because we're starting over and not preserving any history.
        if (restart) {
            const previous = queue.get<AnyQueueData>(id)
            cancel(id, false, previous && { ...previous, status: { ...previous.status, ...status } })
        }

        // Filter out duplicate requests.
        if (queue.has(id)) {
            queue.transition(id, QueueStatus.Duplicate)
            return
        }

        const abortController = new AbortController()
        queue.push<AnyQueueData>(id, { kind, context: { ...context, abortController }, status })

        // Without a context there is nothing to request; the job exists only to render a toast.
        if (!context) {
            queue.transition(id, QueueStatus.Finished)
            return
        }

        queue.transition(id, QueueStatus.Dispatch)

        axios
            .request({
                url: context.url,
                baseURL: context.baseURL || undefined,
                method: context.method || 'POST',
                data: { ...context.data, state: id } as StatefulMessage<RequestT>,
                headers: { SuppressErrors: true },
                signal: abortController.signal,
            })
            .then(
                (response: AxiosResponse) => queue.transition(id, QueueStatus.Queued, context.mapDispatchResponse(response)),
                (response: AxiosError) => {
                    const error = normalizeAxiosError(response)
                    queue.transition(id, QueueStatus.Failed, { error: { message: error?.message, trace: error?.trace } })
                },
            )
    }
}
